import { useMemo, useEffect, useState } from "react";
import SajuContentSlider from "./SajuContentSlider";
import { getAdBannerById, luckTypeToAdBannerId } from "../data/adBannerData";
import { getScoreMessage } from "../data/luckMessages";
import { requestParentResize } from "../util";

type LuckKey = "money" | "love" | "job";

interface IThreeLuckEmbedProps {
  money_score: number | null;
  money_content: string;
  money_sub_title: string;
  money_title: string;
  love_score: number | null;
  love_content: string;
  love_sub_title: string;
  love_title: string;
  job_score: number | null;
  job_content: string;
  job_sub_title: string;
  job_title: string;
  showAds?: boolean;
}

const ThreeLuckEmbed = ({
  money_score,
  money_content,
  money_sub_title,
  money_title,
  love_score,
  love_content,
  love_sub_title,
  love_title,
  job_score,
  job_content,
  job_sub_title,
  job_title,
  showAds = false,
}: IThreeLuckEmbedProps) => {
  const [expandedLuck, setExpandedLuck] = useState<Set<LuckKey>>(
    () => new Set<LuckKey>()
  );

  useEffect(() => {
    requestParentResize([0, 300, 600]);
  }, []);

  useEffect(() => {
    if (!expandedLuck.size) {
      requestParentResize([0, 200, 400]);
      return;
    }
    requestParentResize([0, 300, 600, 900]);
  }, [expandedLuck]);

  const luckRows = useMemo(
    () => [
      {
        key: "money" as const,
        header: "재물운",
        headerClassName: "money",
        title: money_title,
        score: money_score,
        content: money_content,
        subTitle: money_sub_title,
        scoreMessage: getScoreMessage("money", money_score),
      },
      {
        key: "love" as const,
        header: "연애운",
        headerClassName: "love",
        title: love_title,
        score: love_score,
        content: love_content,
        subTitle: love_sub_title,
        scoreMessage: getScoreMessage("love", love_score),
      },
      {
        key: "job" as const,
        header: "직장운",
        headerClassName: "job",
        title: job_title,
        score: job_score,
        content: job_content,
        subTitle: job_sub_title,
        scoreMessage: getScoreMessage("job", job_score),
      },
    ],
    [
      job_content,
      job_score,
      job_sub_title,
      job_title,
      love_content,
      love_score,
      love_sub_title,
      love_title,
      money_content,
      money_score,
      money_sub_title,
      money_title,
    ]
  );

  const toggleLuck = (key: LuckKey) => {
    setExpandedLuck((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderAdBanner = (luckKey: LuckKey) => {
    if (!showAds || !expandedLuck.has(luckKey)) return null;
    const bannerId = luckTypeToAdBannerId[luckKey];
    if (!bannerId) return null;
    const banner = getAdBannerById(bannerId);
    if (!banner) return null;

    return (
      <div className="modal_ad_banner">
        <div className="ad_banner_title">👇 이런 날엔 이거 꼭 읽어보세요</div>
        <SajuContentSlider {...banner} />
      </div>
    );
  };

  return (
    <div className="luck_cards_container">
      {luckRows.map((row) => {
        const isExpanded = expandedLuck.has(row.key);

        return (
          <div key={row.key} className={`luck_card ${row.headerClassName}`}>
            <div className={`luck_card_header ${row.headerClassName}`}>
              <div className="luck_title">{row.header}</div>
              <div className="luck_score">{row.scoreMessage}</div>
            </div>

            <div className="luck_card_body">
              {row.score !== null && (
                <div className={`luck_card_score ${row.headerClassName}`}>
                  {row.score}점
                </div>
              )}
              <div className="luck_card_title">{row.title}</div>
              <div
                className={`luck_card_content ${isExpanded ? "expanded" : ""}`}
              >
                {row.content}
              </div>
              {isExpanded && renderAdBanner(row.key)}
            </div>

            <div className="luck_card_footer">
              <button
                className="luck_card_button"
                onClick={() => toggleLuck(row.key)}
              >
                {isExpanded ? "닫기" : "자세히 읽어보기"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ThreeLuckEmbed;
